import { useMemo } from 'react'
import { useSavedAudioNoteId } from '@renderer/hooks/audio/use-audio-note-selectors'
import type { MeetingViewContext } from '../../views/meeting-view/use-meeting-view-model'

/**
 * View context for opening the Meeting View on a recording that has only just
 * been asked for (Mini's "New Meeting", the Home toolbar, the upcoming row).
 *
 * `startAudioNote` fires `onSuccessfulStart` synchronously — before main has
 * begun the session, and before it clears the finished meeting it's replacing.
 * So a bare `live` open lands while the previous meeting's note is still the
 * live singleton's tail, resolves to it, and latches it into the entry: the new
 * recording then renders under the old meeting's identity, and surfaces the old
 * note once it saves.
 *
 * Naming the note that's already stale is what keeps that window empty. The
 * mark self-expires — the moment `savedNote` isn't that note, it means nothing.
 *
 * The counterpart for opening a meeting that *is* running is
 * `useLiveMeetingIdentity`.
 */
export function useNewMeetingContext(): MeetingViewContext {
  const staleSavedNoteId = useSavedAudioNoteId()

  return useMemo(() => ({ mode: 'live', ...(staleSavedNoteId ? { staleSavedNoteId } : {}) }), [staleSavedNoteId])
}
